import { memo, useState, useEffect, useContext, useMemo, type FunctionComponent, type MouseEventHandler } from 'react';
import isNil from 'lodash/isNil';
import isArray from 'lodash/isArray';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import type { SxProps, Theme } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import DialogContent from '@mui/material/DialogContent';
// Skillmore UI Components
import { bold } from '@empathco/ui-components/src/helpers/intl';
import { isEmptyString } from '@empathco/ui-components/src/helpers/strings';
import SkillLevelDialogTitle from '@empathco/ui-components/src/elements/SkillLevelDialogTitle';
import SkillLevelDialogContent from '@empathco/ui-components/src/elements/SkillLevelDialogContent';
import SkillLevelDialogActions from '@empathco/ui-components/src/elements/SkillLevelDialogActions';
// local imports
import { Skill, SkillLevel, SKILL_LEVEL_MIN, SKILL_LEVEL_TO_MENTOR } from '../models/skill';
import { PATH_SKILL } from '../config/paths';
import useCustomerSettings from '../config/customer';
import { getSkillCurrentLevel, getSkillCurrentLevelOnly, hasStoredLevel } from '../helpers/models';
import { DataContext } from '../context';
import useLevelConfirmation from '../hooks/useLevelConfirmation';
import LevelConfirmCheckbox from '../elements/LevelConfirmCheckbox';

const contentSx: SxProps<Theme> = { pt: 1, pb: 0.75 } as const;
const employeeSx: SxProps<Theme> = { mt: -1, mb: 2.5 } as const;

type EditSkillLevelProps = {
  readonly skill: Skill;
  readonly plain?: boolean;
  readonly readOnly?: boolean;
  readonly editable?: boolean; // `readOnly` content, but the confirm button still saves
  readonly confirmLabel?: string | null;
  readonly removeLabel?: string | null; // replaces `confirmLabel` when the selected level is 0 (`editable` only)
  readonly employeeName?: string | null; // employee being edited: shown as a caption in `editable` mode only
  readonly defaultLevel?: SkillLevel;
  readonly depersonalized?: boolean;
  readonly reducedUI?: boolean | null;
  readonly onUpdate: (skill: Skill, level: SkillLevel, mentoring: boolean, targeting?: boolean) => void;
  readonly onBack?: MouseEventHandler<HTMLButtonElement>;
  readonly onConfirm: MouseEventHandler<HTMLButtonElement>;
  readonly disabled?: boolean | null;
  readonly confirmPending?: boolean | null;
  readonly confirmDisabled?: boolean | null;
  readonly noTargeting?: boolean | null;
  readonly noMentoring?: boolean | null;
  readonly requireLevelConfirm?: boolean | null; // gate eligibility of the context, computed by the parent dialog
  // for Storybook only
  readonly testLevel?: SkillLevel;
  readonly testConfirmed?: boolean; // presets the confirmation checkbox checked
}

const EditSkillLevel: FunctionComponent<EditSkillLevelProps> = ({
  skill,
  plain = false,
  readOnly = false,
  editable = false,
  confirmLabel,
  removeLabel,
  employeeName,
  defaultLevel,
  depersonalized = false,
  reducedUI = false,
  onUpdate,
  onBack,
  onConfirm,
  disabled: parentDisabled = false,
  confirmPending = false,
  confirmDisabled = false,
  noTargeting = false,
  noMentoring = false,
  requireLevelConfirm = false,
  testLevel = 0,
  testConfirmed = false
  // eslint-disable-next-line complexity
}) => {
  const { HAS_MENTORING } = useCustomerSettings();
  const {
    skl: { data: skl, pending: sklPending, failed: sklFailed }, requireSkl,
    preferences: { data: preferences, pending: preferencesPending, failed: preferencesFailed }
  } = useContext(DataContext);
  const { id, abbr, title, is_mentoring, is_target, is_in_demand, inferred_level } = skill || {};
  const currentLevel = isNil(defaultLevel) ? getSkillCurrentLevel(skill) : defaultLevel;
  const isMentoring = HAS_MENTORING && !noMentoring && !depersonalized && Boolean(is_mentoring);
  const isTargeting = !noTargeting && !depersonalized && Boolean(is_target);
  const needsSkl = skill && (isEmptyString(skill.description) || !isArray(skill.level_description));

  const [level, setLevel] = useState<SkillLevel>(testLevel);
  useEffect(() => {
    if (!testLevel) setLevel(currentLevel);
  }, [currentLevel, testLevel]);

  useEffect(() => {
    if (needsSkl && skill && (!skl || skl.id !== skill.id) && skill.abbr) requireSkl?.({ skill_id: skill.abbr });
  }, [needsSkl, requireSkl, skill, skl]);

  const [mentoring, setMentoring] = useState<boolean>(isMentoring);
  useEffect(() => {
    setMentoring(isMentoring);
  }, [id, isMentoring]);

  const [isTarget, setIsTarget] = useState<boolean>(isTargeting);
  useEffect(() => {
    setIsTarget(isTargeting);
  }, [id, isTargeting]);

  const { showLevelConfirm, levelConfirmed, setLevelConfirmed } = useLevelConfirmation(
    skill, level, requireLevelConfirm, testConfirmed
  );

  const canMentorThis = HAS_MENTORING && !noMentoring && !depersonalized && !reducedUI && level >= SKILL_LEVEL_TO_MENTOR;
  const canMentor = HAS_MENTORING && !noMentoring && !depersonalized && !preferencesPending && (
    !preferences || preferencesFailed || Boolean(preferences.mentor_others)
  ) && canMentorThis;

  const sk = needsSkl ? skl : skill;
  useEffect(() => {
    if (sk) onUpdate(sk, level, canMentor ? mentoring : false, noTargeting ? undefined : isTarget);
  }, [canMentor, isTarget, level, mentoring, noTargeting, onUpdate, sk]);

  const pending = needsSkl && (sklPending || !skl);
  const disabled = parentDisabled || pending;

  const withEmployee = editable && !isEmptyString(employeeName);
  const employeeValues = useMemo(() => ({ bold, name: employeeName }), [employeeName]);

  // a manager saving level 0 removes the skill from the employee's profile: name the action for what it does,
  // but only when there is something to remove — a catalog skill they never claimed sits at level 0 already.
  // `hasStoredLevel`, not `hasCurrentLevel`: a stored level superseded by a newer inference is still deleted,
  // and never labelling a deletion "Save Changed Level" matters more than the occasional over-labelling of an
  // inference-only skill, whose payload carries `current_level` with nothing stored behind it
  const actionLabel = editable && removeLabel && level === SKILL_LEVEL_MIN && hasStoredLevel(skill)
    ? removeLabel : confirmLabel;

  return (
    <>
      <SkillLevelDialogTitle
          route={PATH_SKILL}
          reducedUI={reducedUI}
          abbr={abbr}
          title={title}
          isTarget={is_target}
          isInDemand={is_in_demand}
          disabled={disabled}
          plain={Boolean((plain || depersonalized) && !readOnly)}
      />
      <DialogContent sx={contentSx}>
        {withEmployee ? (
          <Alert severity="info" variant="outlined" sx={employeeSx}>
            <FormattedMessage id="supervisor.dialog.editing_for" values={employeeValues}/>
          </Alert>
        ) : undefined}
        <SkillLevelDialogContent
            readOnly={readOnly}
            noTargeting={noTargeting}
            depersonalized={depersonalized}
            currentLevel={depersonalized ? undefined : getSkillCurrentLevelOnly(skill)}
            inferredLevel={depersonalized ? undefined : inferred_level}
            description={(needsSkl ? skl : skill)?.description}
            levelDescription={(needsSkl ? skl : skill)?.level_description}
            disabled={disabled}
            failed={needsSkl && sklFailed ? true : undefined}
            pending={pending}
            loading={!noMentoring && !depersonalized && preferencesPending ? true : undefined}
            canMentor={canMentor}
            level={level}
            setLevel={setLevel}
            mentoring={mentoring}
            setMentoring={setMentoring}
            isTarget={isTarget}
            setIsTarget={setIsTarget}
        />
        {showLevelConfirm ? (
          <Box mx={0.5} mb={2.5} mt={-1}>
            <LevelConfirmCheckbox
                label="skill.level_confirm.checkbox"
                checked={levelConfirmed}
                onChange={setLevelConfirmed}
                disabled={disabled}
            />
          </Box>
        ) : undefined}
      </DialogContent>
      <SkillLevelDialogActions
          readOnly={Boolean(readOnly && !editable)}
          confirmLabel={actionLabel}
          onConfirm={onConfirm}
          onBack={onBack}
          visible={!pending && (!needsSkl || !sklFailed)}
          pending={confirmPending}
          disabled={disabled || confirmDisabled || (showLevelConfirm && !levelConfirmed)}
          withBorder
      />
    </>
  );
};

export default memo(EditSkillLevel);
